// ============================================================
// usePlayback — React hook for PlaybackEngine
// ============================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import * as Tone from 'tone';
import { playbackEngine, type PlaybackCursor } from '../../core/playbackEngine';
import type { Song, Section } from '../../core/types';

interface UsePlaybackOptions {
  song: Song;
  loopSection?: Section | null;
}

interface UsePlaybackReturn {
  playing:          boolean;
  cursor:           PlaybackCursor | null;
  play:             () => Promise<void>;
  stop:             () => void;
  toggle:           () => Promise<void>;
  isLoading:        boolean;
  showClick:        boolean;
  toggleClick:      () => void;
  chordVolume:      number;   // 0–1
  setChordVolume:   (v: number) => void;
  clickVolume:      number;   // 0–1
  setClickVolume:   (v: number) => void;
}

// Convert 0–1 slider value to dB for the chord synth.
// 0 → -40dB (near silent), 0.5 → -12dB (default), 1 → 0dB (full)
function sliderToDb(v: number): number {
  if (v <= 0) return -60;
  return -40 + v * 40;
}

export function usePlayback({
  song,
  loopSection = null,
}: UsePlaybackOptions): UsePlaybackReturn {
  const [playing,     setPlaying]     = useState(false);
  const [cursor,      setCursor]      = useState<PlaybackCursor | null>(null);
  const [isLoading,   setIsLoading]   = useState(false);
  const [showClick,   setShowClick]   = useState(false);
  const [chordVolume, setChordVolumeState] = useState(0.7);  // default ~-12dB
  const [clickVolume, setClickVolumeState] = useState(0.8);  // default loud

  const showClickRef   = useRef(showClick);
  const playingRef     = useRef(playing);
  const songRef        = useRef(song);
  const clickVolumeRef = useRef(clickVolume);

  useEffect(() => { showClickRef.current   = showClick;   }, [showClick]);
  useEffect(() => { playingRef.current     = playing;     }, [playing]);
  useEffect(() => { songRef.current        = song;        }, [song]);
  useEffect(() => { clickVolumeRef.current = clickVolume; }, [clickVolume]);

  useEffect(() => {
    const unsubCursor = playbackEngine.addCursorCallback(setCursor);
    const unsubState  = playbackEngine.addStateCallback((state) => {
      setPlaying(state === 'playing');
    });
    return () => {
      unsubCursor();
      unsubState();
      playbackEngine.stop();
    };
  }, []);

  const play = useCallback(async () => {
    setIsLoading(true);
    try {
      await Tone.start();
      await playbackEngine.load(songRef.current, {
        showClick:   showClickRef.current,
        clickVolume: clickVolumeRef.current,
      });
      playbackEngine.play();
    } finally {
      setIsLoading(false);
    }
  }, []);

  const stop = useCallback(() => {
    playbackEngine.stop();
  }, []);

  const toggle = useCallback(async () => {
    if (playingRef.current) stop(); else await play();
  }, [play, stop]);

  const toggleClick = useCallback(() => {
    const next = !showClickRef.current;
    showClickRef.current = next;
    setShowClick(next);

    if (playingRef.current) {
      (async () => {
        await playbackEngine.load(songRef.current, {
          showClick:   next,
          clickVolume: clickVolumeRef.current,
        });
        playbackEngine.play();
      })();
    }
  }, []);

  // Chord volume: update engine live (works even while playing
  // since setVolume directly sets the synth's volume node).
  const setChordVolume = useCallback((v: number) => {
    setChordVolumeState(v);
    playbackEngine.setVolume(sliderToDb(v));
  }, []);

  // Click volume: update engine live via setClickVolume which
  // directly modifies the Gain node if click is currently active.
  const setClickVolume = useCallback((v: number) => {
    clickVolumeRef.current = v;
    setClickVolumeState(v);
    playbackEngine.setClickVolume(v);
  }, []);

  return {
    playing, cursor, play, stop, toggle, isLoading,
    showClick, toggleClick,
    chordVolume, setChordVolume,
    clickVolume, setClickVolume,
  };
}